import { RadioGroup } from '@/components/common/RadioGroup'
import { EmailEngagementTarget } from '@/types/touchPoint'

interface EngagementTargetSelectProps {
  /** Current engagement stage, or null (treated as None — no gate). */
  value: EmailEngagementTarget | null
  onChange: (value: EmailEngagementTarget | null) => void
}

const OPTIONS = [
  { value: EmailEngagementTarget.None, label: 'Everyone', description: 'No engagement gate' },
  { value: EmailEngagementTarget.Unopened, label: "Didn't open", description: 'Got a prior email, never opened' },
  { value: EmailEngagementTarget.OpenedNotClicked, label: 'Opened, no click', description: 'Opened but never clicked through' },
  { value: EmailEngagementTarget.ClickedNotConverted, label: 'Clicked, no convert', description: 'Reached the landing page, did not convert' },
]

/**
 * Engagement-stage selector for a Download nurture touchpoint (ORB-359).
 *
 * The materializer only sends the email to recipients still at the chosen stage, keyed on their own
 * prior sends for this web; converters drop out automatically. "Everyone" (None) disables the gate —
 * the default for every non-nurture email. The rule editor pre-fills the per-email-type default from
 * the EmailRules lookup when an asset is picked; the user can override.
 */
export function EngagementTargetSelect({ value, onChange }: EngagementTargetSelectProps) {
  return (
    <RadioGroup
      name="EngagementTarget"
      layout="grid"
      options={OPTIONS}
      value={value ?? EmailEngagementTarget.None}
      onChange={next => {
        // "Everyone" (None) maps back to null — the persisted "no gate" representation — so a fresh
        // Everyone selection never drifts from a null baseline in the dirty-check.
        const target = next as EmailEngagementTarget
        onChange(target === EmailEngagementTarget.None ? null : target)
      }}
      helperText="Download nurtures only send to recipients still at this stage; converters drop out automatically. Everyone = no gate."
    />
  )
}
